import { useMemo, useState } from "react";
import { useFoodProfileSummary } from "@apiHooks/useFoodProfileSummary";
import useQueryParam from "@hooks/useQueryParam";
import { FoodSectionEnum } from "@pages/Food/Food.constants";

/**
 * Page state shared by both Food Profile versions: the profile data for `?q=`,
 * and the sections nav, which lists a section once it reports having data.
 */
export default function useFoodProfilePage() {
    const [hasSocial, setHasSocial] = useState(false);
    const [hasReports, setHasReports] = useState(false);
    const [hasCharts, setHasCharts] = useState(false);
    const [hasLaunches, setHasLaunches] = useState(false);

    const word = useQueryParam('q');
    const { data: { summary, menu, consumer }, isLoading } = useFoodProfileSummary(word);
    const hasConsumer = consumer.hasData;

    const navItems = useMemo(
        () => {
            const result = [
                {
                    id: FoodSectionEnum.MenuAndConsumer,
                    title: hasConsumer ? 'Menu and Consumer' : 'Menu',
                }
            ];

            if (hasSocial) result.push({ id: FoodSectionEnum.Social, title: 'Social' });
            if (hasReports) result.push({ id: FoodSectionEnum.Reports, title: 'Reports' });
            if (hasCharts) result.push({ id: FoodSectionEnum.Charts, title: 'Charts' });
            if (hasLaunches) result.push({ id: FoodSectionEnum.Launches, title: 'Launches' });

            return result;
        },
        [hasConsumer, hasReports, hasCharts, hasLaunches, hasSocial]
    );

    /** Which sections have data; the refresh nav is built from this. */
    const sectionsWithData = useMemo(
        () => ({
            consumer: hasConsumer,
            social: hasSocial,
            reports: hasReports,
            charts: hasCharts,
            launches: hasLaunches,
        }),
        [hasConsumer, hasSocial, hasReports, hasCharts, hasLaunches]
    );

    return {
        isLoading,
        summary,
        navItems,
        sectionsWithData,
        sections: { summary, menu, consumer, setHasSocial, setHasReports, setHasCharts, setHasLaunches },
    };
}
